import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_BackgroundEditor = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/background-editor',
			name: 'Photo Editor to Change Background | Edit Background Online | Lift',
			description:
				'Replace photo backgrounds instantly with AI. No watermark. Upload photo, describe your ideal backdrop, and create professional product images in seconds. Try now!',
			url: 'https://liftapp.ai/background-editor',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/background-editor#product' },
				{ '@id': 'https://liftapp.ai/background-editor#howto' },
				{ '@id': 'https://liftapp.ai/background-editor#benefits' },
				{ '@id': 'https://liftapp.ai/background-editor#faq' },
				{ '@id': 'https://liftapp.ai/background-editor#why-lift' },
				{ '@id': 'https://liftapp.ai/background-editor#explore-tools' },
				{ '@id': 'https://liftapp.ai/background-editor#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/background-editor#product',
			name: 'AI Background Editor',
			description:
				'Replace photo backgrounds instantly with AI. No watermark. Upload photo, describe your ideal backdrop, and create professional product images in seconds.',
			url: 'https://liftapp.ai/background-editor',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/background-editor#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift edits the background automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/background-editor#benefits',
			name: 'Who can benefit from Background Editor?',
			description:
				"Lift's AI Background Editor is useful for small business owners, content creators, marketers, freelancers, and educators who want professional product photos and polished visuals.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Small business owners — Create professional product photos without expensive studios or photographers.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Content creators — Swap backgrounds fast to match trends, aesthetics, and campaigns.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Marketers & SMM managers — Scale visual production for ads, banners, and landing pages.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Freelancers — Deliver client-ready visuals without advanced design skills.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Create polished visuals for presentations, projects, and social posts.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Background Editor by Lift',
				description:
					'Before-and-after comparison of a photo with a replaced background.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/background-editor#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Background Editor?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Background Editor is a premium online tool that instantly replaces photo backgrounds with AI-generated scenes with pixel-perfect precision — ideal for product photos, profile pictures, or graphic design.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Background Editor work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload your photo to Lift's background editor tool. Our AI automatically detects the subject and replaces the background in seconds — no photo editing experience needed. Download up to 20 edited images with professional backgrounds.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Background Editor uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking edits without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/background-editor#why-lift',
			name: "Why Lift's AI Background Editor stands out",
			description:
				"Lift's Background Editor combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift AI Background Editor',
				description:
					'Before-and-after example of photo background replacement in photo editing.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/background-editor#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/background-editor#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Background Editor',
					item: 'https://liftapp.ai/background-editor',
				},
			],
		},
	],
};
